import { useRef, useState } from 'react'

import { cn } from '@gto/ui/cn'
import { ChevronDownIcon } from '@gto/ui/icons/solid'
import { Menu } from '@gto/ui/menu'
import type { Tone } from '@gto/ui/tone'

type LogoSelectorProps = {
  items: LogoSelectorItem[]
  button: LogoSelectorButton
  /**
   * Surface the selector sits on. `onPrimary` (the Fair Play bar's primary-green
   * fill) paints the chevron in the on-primary ink in every state — the default
   * light ink and its green hover both vanish on green — leaving only the 180°
   * flip to mark "open".
   */
  tone?: Tone
  /**
   * Inline style for the portaled positioner — use to raise the menu's
   * z-index above an app-specific high-z navbar (base-ui's stacking context
   * lives on the positioner, not the popup).
   */
  positionerStyle?: React.CSSProperties
}

export type LogoSelectorItem = {
  label: string
  logo: React.ReactNode
  link: string
  /**
   * In-app items pass a handler that prevents the default anchor navigation
   * and routes client-side; external items omit it and hard-navigate via href.
   */
  onClick?: React.MouseEventHandler<HTMLAnchorElement>
}

export type LogoSelectorButton = {
  label: string
  logo: React.ReactNode
  onClick: () => void
}

export const LogoSelector = ({
  items,
  button,
  tone = 'default',
  positionerStyle,
}: LogoSelectorProps) => {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)

  return (
    <div
      ref={anchorRef}
      className="flex flex-row items-center gap-2 pl-2"
    >
      <button
        aria-label={button.label}
        className="flex"
        tabIndex={-1}
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          button.onClick()
        }}
      >
        {button.logo}
      </button>

      <Menu.Root
        open={open}
        onOpenChange={setOpen}
      >
        <Menu.Trigger
          aria-label={`${button.label} menu`}
          className={cn(
            'flex size-5 cursor-pointer items-center justify-center transition-all',
            tone === 'onPrimary'
              ? 'text-primary-contrastText hover:text-primary-contrastText'
              : 'text-text-action-active hover:text-primary-main',
            open && '-rotate-180',
            open && tone === 'default' && 'text-primary-main',
          )}
        >
          <ChevronDownIcon className="size-5" />
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner
            align="start"
            anchor={anchorRef}
            sideOffset={8}
            style={positionerStyle}
          >
            <Menu.Popup>
              <Menu.Group>
                {items.map((item) => (
                  <Menu.LinkItem
                    key={item.label}
                    closeOnClick
                    aria-label={item.label}
                    href={item.link}
                    onClick={item.onClick}
                  >
                    {/* Wrapped so the wide wordmark isn't a direct child of the
                        row — the row's leading-icon rule (`[&>svg]:size-4`)
                        would clamp it to a 16px square. */}
                    <span className="flex shrink-0">{item.logo}</span>
                  </Menu.LinkItem>
                ))}
              </Menu.Group>
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    </div>
  )
}
